import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { GlobalErrorHandler, RouteReusableStrategy, SharedModule } from './@shared';
import { RouteReuseStrategy } from '@angular/router';
import { ShellModule } from './modules/shell';
import { HomeModule } from './modules/home/home.module';
import { SpinnerOverlayInterceptor } from './@shared/interceptors/spinner-overlay.interceptor';
import { SessionExpiryInterceptor } from './@shared/interceptors/session-expiry.interceptor';
import { StandardV1Module } from './modules/standard/v1/standard.v1.module';
import { RiteaidV1Module } from './modules/organizations/riteaid/v1/riteaid.v1.module';
import { WalgreensV1Module } from './modules/organizations/walgreens/v1/walgreens.v1.module';
import { DemoV1Module } from './modules/organizations/demo/v1/demo.v1.module';
import { QaAutomationV1Module } from './modules/organizations/qaautomation/v1/qaautomation.v1.module';
import { UnfiV1Module } from './modules/organizations/unfi/v1/unfi.v1.module';
import { environment as env } from '../environments/environment';
import { useAuth0Provider } from './@shared/auth-mode';
import { KrogerV1Module } from './modules/organizations/kroger/v1/kroger.v1.module';
import { CustomAuthHttpInterceptor } from './@shared/interceptors/custom-authHttpInterceptor';
import { KeycloakAngularModule } from 'keycloak-angular';
import { APP_INITIALIZER } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { AuthModule, AuthHttpInterceptor, AuthConfig } from '@auth0/auth0-angular';

export function auth0AngularModuleConfig(): AuthConfig {
  if (useAuth0Provider()) {
    return {
      ...env.auth,
      httpInterceptor: {
        ...env.httpInterceptor,
      },
      "organization": env.auth.organization,
      "scope" : "openid profile email"
    }
  }
  return {
    domain: 'unused.auth.invalid',
    clientId: 'unused',
    skipRedirectCallback: true,
  };
}

export function initializeKeycloak(keycloak: KeycloakService) {
  return () => {
    if (env.appMode === 'shopify' || useAuth0Provider()) {
      return Promise.resolve(true);
    }
    return keycloak
      .init({
        config: {
          url: env.keycloak.url,
          realm: env.keycloak.realm,
          clientId: env.keycloak.clientId,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          pkceMethod: 'S256',
          useNonce: false,
          silentCheckSsoRedirectUri:
            window.location.origin + '/pr1core/assets/silent-check-sso.html',
        },
      })
      .catch((reason: unknown) => {
        const message =
          reason instanceof Error
            ? reason.message
            : reason != null
              ? String(reason)
              : 'Keycloak init failed (check redirect URIs / Web Origins, token endpoint errors, or try useNonce: false for silent check-sso)';
        console.error('Keycloak init failed:', reason);
        return Promise.reject(reason instanceof Error ? reason : new Error(message));
      });
  };
}
import { ShopifyJwtInterceptor } from './@shared/interceptors/shopify-jwt-interceptor';
import { ShopifyV1Module } from './modules/organizations/shopify/v1/shopify.v1.module';
import { ARGV1Module } from './modules/organizations/arg/v1/arg.v1.module';
import { TractorSupplyV1Module } from './modules/organizations/tractorsupply/v1/tractorsupply.v1.module';
import { SchnucksV1Module } from './modules/organizations/schnucks/v1/schnucks.v1.module';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AuthModule.forRoot(auth0AngularModuleConfig()),
    SharedModule,
    ShellModule,
    HomeModule,
    StandardV1Module,
    KrogerV1Module,
    RiteaidV1Module,
    WalgreensV1Module,
    DemoV1Module,
    QaAutomationV1Module,
    UnfiV1Module,
    ShopifyV1Module,
    ARGV1Module,
    TractorSupplyV1Module,
    SchnucksV1Module,
    AppRoutingModule,
    KeycloakAngularModule,
  ],
  providers: [
    AuthHttpInterceptor,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    ShopifyJwtInterceptor,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: RouteReuseStrategy, useClass: RouteReusableStrategy },
    // First, so it also sees the Auth0 token errors raised by the auth interceptor below it.
    { provide: HTTP_INTERCEPTORS, useClass: SessionExpiryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SpinnerOverlayInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CustomAuthHttpInterceptor, multi: true }
  ],
  bootstrap: [AppComponent],
})

export class AppModule { }
